/**
 * Template Analytics Dashboard Page
 *
 * Shows install trends, top templates, ratings, and summary stats
 * for the current user's marketplace templates.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { useAuthorAnalytics } from '../hooks/useTemplateAnalytics';

type TimeRange = 7 | 30 | 90;

const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

export default function TemplateAnalytics() {
  const [days, setDays] = useState<TimeRange>(30);
  const { data, isLoading, error } = useAuthorAnalytics(days);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-red-600">Failed to load analytics. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Template Analytics</h1>
            <p className="mt-1 text-sm text-gray-500">
              Track installs, trends, and performance for your marketplace templates
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/templates">Back to Marketplace</Link>
            </Button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="mb-6 flex gap-2">
          {TIME_RANGES.map((range) => (
            <Button
              key={range.value}
              variant={days === range.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(range.value)}
            >
              {range.label}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <LoadingSkeleton key={i} className="h-28" />
            ))}
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Templates</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{data.totalTemplates}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Installs</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{data.totalInstalls}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Installs ({days}d)</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{data.installsInPeriod}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Average Rating</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {data.averageRating !== null
                      ? `${data.averageRating.toFixed(1)} / 5`
                      : 'N/A'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Install Trend Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Install Trend</CardTitle>
                <CardDescription>
                  Daily install count over the last {days} days
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(val: string) => {
                          const d = new Date(val);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                        interval={Math.max(0, Math.floor(data.trend.length / 7) - 1)}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        labelFormatter={(val: string) => new Date(val).toLocaleDateString()}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                        name="Installs"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-12 text-center text-gray-500">
                    No trend data available
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Top Templates Table */}
            <Card>
              <CardHeader>
                <CardTitle>Top Templates</CardTitle>
                <CardDescription>Your most installed templates</CardDescription>
              </CardHeader>
              <CardContent>
                {data.topTemplates.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Template
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            Installs
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            Rating
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                            Forks
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {data.topTemplates.map((t) => (
                          <tr key={t.templateId} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                              {t.templateName}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-600">
                              {t.installs}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-600">
                              {t.rating !== null ? t.rating.toFixed(1) : '-'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-600">
                              {t.forkCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="py-8 text-center text-gray-500">
                    No templates yet.{' '}
                    <Link to="/templates" className="text-blue-600 hover:underline">
                      Create your first template
                    </Link>
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </div>
  );
}
